// Image cache utility to reduce Supabase storage requests
class ImageCache {
  constructor() {
    this.cache = new Map();
    this.maxCacheSize = 500; // Max 500 cached image URLs
    this.cacheExpiry = 24 * 60 * 60 * 1000; // 24 hours
  }

  /**
   * Get a cached image URL or store a new one
   * @param {string} key - Unique key for the image (e.g., user ID + size)
   * @param {string} url - Image URL to cache
   * @returns {string} - The cached or new URL
   */
  get(key, url) {
    const cached = this.cache.get(key);
    const now = Date.now();

    // Return cached URL if it exists and hasn't expired
    if (cached && (now - cached.timestamp) < this.cacheExpiry) {
      return cached.url;
    }

    // Cache the new URL
    this.set(key, url);
    return url;
  }

  /**
   * Store an image URL in the cache
   * @param {string} key - Unique key for the image
   * @param {string} url - Image URL to cache
   */
  set(key, url) {
    // Remove oldest entries if cache is full
    if (this.cache.size >= this.maxCacheSize) {
      const oldestKey = this.cache.keys().next().value;
      this.cache.delete(oldestKey);
    }

    this.cache.set(key, {
      url,
      timestamp: Date.now()
    });
  }

  /**
   * Create a cache key for user avatars
   * @param {string} userId - User ID
   * @param {string} size - Size string (e.g., 'w-10 h-10')
   * @param {string} avatarUrl - Avatar URL to include in cache key for uniqueness
   * @returns {string} - Cache key
   */
  getUserAvatarKey(userId, size, avatarUrl = '') {
    // Include a hash of the avatar URL to ensure cache invalidation when avatar changes
    const urlHash = avatarUrl ? this._simpleHash(avatarUrl) : 'no-avatar';
    return `user_${userId}_${size}_${urlHash}`;
  }

  /**
   * Create a cache key for team logos
   * @param {string} teamId - Team ID
   * @param {string} size - Size string
   * @param {string} logoUrl - Logo URL to include in cache key for uniqueness
   * @returns {string} - Cache key
   */
  getTeamLogoKey(teamId, size, logoUrl = '') {
    // Include a hash of the logo URL to ensure cache invalidation when logo changes
    const urlHash = logoUrl ? this._simpleHash(logoUrl) : 'no-logo';
    return `team_${teamId}_${size}_${urlHash}`;
  }

  /**
   * Create a cache key for tournament logos
   * @param {string} tournamentId - Tournament ID
   * @param {string} size - Size string
   * @returns {string} - Cache key
   */
  getTournamentLogoKey(tournamentId, size) {
    return `tournament_${tournamentId}_${size}`;
  }

  /**
   * Clear expired entries from cache
   */
  clearExpired() {
    const now = Date.now();
    for (const [key, value] of this.cache.entries()) {
      if ((now - value.timestamp) >= this.cacheExpiry) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Clear all cache entries
   */
  clear() {
    this.cache.clear();
  }

  /**
   * Clear team logo cache entries only
   */
  clearTeamLogos() {
    for (const [key] of this.cache.entries()) {
      if (key.startsWith('team_')) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Get cache statistics
   * @returns {Object} - Cache stats
   */
  getStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxCacheSize,
      expiry: this.cacheExpiry
    };
  }

  /**
   * Simple hash function for creating short, consistent hashes
   * @param {string} str - String to hash
   * @returns {string} - Hash string
   */
  _simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }
}

// Export singleton instance
export const imageCache = new ImageCache();

// Clear expired entries every 30 minutes
setInterval(() => {
  imageCache.clearExpired();
}, 30 * 60 * 1000);